import React, { useCallback } from "react";
import styled from "@emotion/styled";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductCard from "@components/card/ProductCard";
import { GET_PRODUCT_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useAxios from "@libs/axios/useAxios";
import { useInfiniteQuery } from "@tanstack/react-query";
import BackHeader from "@components/header/BackHeader";
import CategoryFilterBar from "./_components/CategoryFilterBar";
import FreeShippingFee from "./_components/FreeShippingFee";
import SortFilter from "./_components/SortFilter";
import ConditionFilter from "./_components/ConditionFilter";
import ColorFilter from "./_components/ColorFilter";
import CategoryFilterSection from "./_components/CategoryFilterSection";
import { SearchBrand } from "./_components";
import useFilterStepStore from "@store/CategoryFilter/useFilterStepStore";
import { useTheme } from "@emotion/react";
import BrandCategoryFilter from "./_components/BrandCategoryFilter";
import { useTranslation } from "react-i18next";
import useIntersection from "@hook/useIntersection";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.8rem",
});

const ContentWrapper = styled.div({
  width: "100%",
  display: "flex",
  flexDirection: "column",
});

const ProductWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1.6rem",
});

const TitleBox = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 2rem",
});

const Title = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "2rem",
  fontWeight: 600,
  margin: 0,
  lineHeight: "2rem",
  display: "flex",
  justifyContent: "space-between",
}));

const ListContainer = styled.div(
  () =>
    ({
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
    }) as const,
);

const CategorySearchDetail = () => {
  const { axiosInstance } = useAxios();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category_id");
  const categoryName = searchParams.get("categoryName");
  const orderBy = searchParams.get("order_by");
  const brandId = searchParams.get("brand_id");
  const productCondition = searchParams.get("product_condition");
  const colorId = searchParams.get("color_id");
  const isFreeShipping = searchParams.get("is_free_shipping");
  const lang = window.localStorage.getItem("language");
  const { step, setStep } = useFilterStepStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  // 검색 API 호출
  const getProductList = useCallback(
    async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam,
        limit: LOAD_SIZE_10,
        category_id: categoryId,
        order_by: orderBy,
        brand_id: brandId,
        product_condition: productCondition,
        color_id: colorId,
        is_free_shipping: isFreeShipping,
      };

      const searchParams = new URLSearchParams(
        Object.entries(params).filter(([_, value]) => value != null) as [
          string,
          string,
        ][],
      );

      const res = await axiosInstance.get(`${GET_PRODUCT_URL}?${searchParams}`);
      return res.status === 200 ? res.data : null;
    },
    [axiosInstance],
  );

  // 무한 스크롤 쿼리
  const {
    data: productList,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [
      "categoryProductList",
      categoryId,
      orderBy,
      brandId,
      productCondition,
      colorId,
      isFreeShipping,
    ],

    queryFn: getProductList,
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages?.length + 1
        : undefined,
    enabled: !!categoryId,
  });

  const stepArr = [
    {
      key: 1,
      component: <CategoryFilterSection />,
    },
    {
      key: 2,
      component: <SortFilter />,
    },
    {
      key: 3,
      component: <BrandCategoryFilter />,
    },
    {
      key: 4,
      component: <ConditionFilter />,
    },
    {
      key: 5,
      component: <ColorFilter />,
    },
    {
      key: 6,
      component: <FreeShippingFee />,
    },
  ];

  return (
    <Container>
      {/* 스텝 상태에 따른 화면 */}
      {step === 0 ? (
        // 상품 목록 화면
        <>
          <BackHeader
            title={categoryName ? categoryName : ""}
            onClick={() => navigate("/search")}
          />

          {/* 필터 바 */}
          <CategoryFilterBar setStep={setStep} />

          <ContentWrapper>
            <ProductWrapper>
              <TitleBox>
                <Title>{t("상품")}</Title>
              </TitleBox>

              <ListContainer>
                {productList?.pages?.map((page) => {
                  return page?.data?.list?.map((product: any) => (
                    <ProductCard
                      key={product.product_id}
                      id={product.product_id}
                      name={
                        lang === "en"
                          ? product.product_name_en
                          : product.product_name_ko
                      }
                      brand={
                        lang === "en"
                          ? product.brand_name_en
                          : product.brand_name_ko
                      }
                      src={product.thumbnail_url}
                      price={product.price}
                      likeCount={product.like_total}
                      chatCount={product.qna_total}
                      haveLike
                      soldout={product.is_sold_out === 1}
                      isLiked={product.is_liked === 1}
                      condition={product.product_condition}
                    />
                  ));
                })}

                <div ref={setObserverTarget} />
              </ListContainer>
            </ProductWrapper>
          </ContentWrapper>
        </>
      ) : (
        // 필터 상세 화면
        <>
          <ContentWrapper>{stepArr[step - 1]?.component}</ContentWrapper>
        </>
      )}
    </Container>
  );
};

export default CategorySearchDetail;
